import React, { useEffect, useState } from 'react';
import { Icon } from './inboxIcons';

// Support → Open → Wholesale registrations: one registration waiting for approval, with what the team can do.
// Approve (gives the contact a role in Shopify, then emails + WhatsApps them), ask for details by email (editable),
// not approved (reason for the team, optional polite email), and notes. Every Support user can act.
const when = (ms) => (ms ? new Date(ms).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '');
const waited = (h) => (h >= 48 ? `${Math.floor(h / 24)} days` : `${h} h`);

function InboxWholesale({ item, api, toast, onBack, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [panel, setPanel] = useState(null); // 'ask' | 'reject' | null
  const [askText, setAskText] = useState('');
  const [reason, setReason] = useState('');
  const [notify, setNotify] = useState(false);
  const [note, setNote] = useState('');
  useEffect(() => { setPanel(null); setAskText(item?.askDraft || ''); setReason(''); setNotify(false); setNote(''); }, [item?.key, item?.askDraft]);
  if (!item) return null;

  const act = async (action, body, done) => {
    setBusy(action);
    try {
      const r = await api(`/api/v1/support/wholesale/${item.key}/${action}`, 'POST', body);
      toast(done(r));
      setPanel(null);
      await onChanged();
    } catch (err) { toast(err.message, true); } finally { setBusy(null); }
  };
  const approve = () => {
    if (!window.confirm(`Approve ${item.company || item.name} for wholesale? They get wholesale prices and an email${item.phone ? ' + WhatsApp' : ''}.`)) return;
    act('approve', {}, (r) => `Approved (${r.role}).${r.email ? ' Email sent.' : ''}${r.whatsapp ? ' WhatsApp sent.' : ''}`);
  };
  const overdue = item.ageHours > 48;

  return (
    <section className="cx-thread" aria-label={`Wholesale registration from ${item.company || item.name}`}>
      <header className="cx-t-head">
        <button className="cx-btn cx-btn-icon cx-btn-ghost cx-back" onClick={onBack} aria-label="Back to list"><Icon name="back" /></button>
        <div className="cx-t-who"><b>{item.company || item.name}</b><span>Wholesale registration · waiting {waited(item.ageHours)}</span></div>
        <div className="cx-t-actions">
          <button className="cx-btn cx-btn-primary" onClick={approve} disabled={!!busy}><Icon name="check" />{busy === 'approve' ? 'Approving…' : 'Approve'}</button>
          <button className="cx-btn cx-hide-sm" onClick={() => setPanel(panel === 'ask' ? null : 'ask')} disabled={!!busy || !item.email}><Icon name="mail" />Ask for details</button>
          <button className="cx-btn cx-hide-sm" onClick={() => setPanel(panel === 'reject' ? null : 'reject')} disabled={!!busy}><Icon name="x" />Not approved</button>
          {item.adminUrl && <a className="cx-btn cx-btn-icon cx-btn-ghost" href={item.adminUrl} target="_blank" rel="noreferrer" title="Open in Shopify" aria-label="Open in Shopify"><Icon name="shop" /></a>}
        </div>
      </header>
      {overdue && <div className="cx-banner"><Icon name="clock" />Waiting {waited(item.ageHours)}. The website promises approval in 24 to 48 hours.</div>}
      <div className="cx-msgs">
        <div className="cx-cart-facts">
          <span><Icon name="user" size={13} />{item.name || 'No contact name'}</span>
          <span><Icon name="mail" size={13} />{item.email || 'No email'}</span>
          <span><Icon name="phone" size={13} />{item.phone || 'No phone'}</span>
          {(item.address || item.city) && <span><Icon name="shop" size={13} />{item.address || item.city}</span>}
          <span><Icon name="clock" size={13} />Registered {when(Date.parse(item.createdAt))}</span>
        </div>

        {panel === 'ask' && (
          <div className="cx-fact">
            <b>Ask for details (email from wholesale@)</b>
            <textarea className="cx-input" rows={10} value={askText} onChange={(e) => setAskText(e.target.value)} aria-label="Email text" />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="cx-btn" onClick={() => setPanel(null)}>Cancel</button>
              <button className="cx-btn cx-btn-primary" disabled={!!busy || askText.trim().length < 20} onClick={() => act('ask', { text: askText }, () => 'Email sent. Their reply comes into Support.')}>{busy === 'ask' ? 'Sending…' : 'Send email'}</button>
            </div>
          </div>
        )}
        {panel === 'reject' && (
          <div className="cx-fact">
            <b>Not approved</b>
            <input className="cx-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason, for the team (e.g. not a business, duplicate, no reply)" aria-label="Reason" />
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}><input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} disabled={!item.email} />Send them a polite "not approved" email</label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="cx-btn" onClick={() => setPanel(null)}>Cancel</button>
              <button className="cx-btn cx-btn-primary" disabled={!!busy || !reason.trim()} onClick={() => act('reject', { reason, notify }, () => 'Marked not approved.')}>{busy === 'reject' ? 'Saving…' : 'Mark not approved'}</button>
            </div>
          </div>
        )}

        <div className="cx-fact">
          <b>Notes</b>
          {(item.notes || []).length === 0 ? <span className="cx-hint">No notes yet. Who is checking this, what was asked, what they said.</span> : (
            [...item.notes].sort((a, b) => (a.atMs || 0) - (b.atMs || 0)).map((n, i) => (
              <div key={`${n.atMs}-${i}`} style={{ fontSize: 13 }}><span className="cx-hint">{when(n.atMs)} · {n.by || 'system'}</span><div>{n.text}</div></div>
            ))
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="cx-input" style={{ flex: 1 }} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note" aria-label="Add a note" onKeyDown={(e) => { if (e.key === 'Enter' && note.trim()) act('note', { text: note }, () => 'Note added.'); }} />
            <button className="cx-btn" disabled={!!busy || !note.trim()} onClick={() => act('note', { text: note }, () => 'Note added.')}>Add</button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default InboxWholesale;
